import React, { ChangeEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { Box, Divider, Grid, Stack, Typography, styled } from '@mui/material';
import Select from 'components/Select';
import Button from 'components/Button';
import CheckboxSingle from 'components/CheckboxSingle';
import TextField from 'components/TextField';
import {
  createCompany,
  getCompanyLinkNonEmployeeSponsoredAccount,
  getSignedPutUrl,
  updateCompany,
  uploadLogoCompanyToAWS,
} from 'data/api/company.api';
import { DASHBOARD_ROUTES } from 'Routes';
import {
  CompanyInfo,
  CompanyOverview,
  CompanyRegistrationLink,
} from '@data/models/company.model';
import { useDispatch, useSelector } from 'react-redux';
import {
  getPurchasedEventType,
  saveCompany,
  selectCompany,
} from 'store/companySlide';
import { STATES } from 'data/constants/static';
import { CompanyType } from 'pages/company/new-company/interface';
import { SignedPutUrl } from 'data/models/signed-put-rl.model';
import TextFieldNumber from 'components/TextFieldNumber';
import TextFieldWithMask from 'components/TextFieldWithMask';
import { useAppSelector } from 'store/hooks';
import MultiSelect from './MultiSelect';
import { MaterialUISwitch } from 'components/Switch';
import { SearchBox } from '../SearchBox';
import { ModalDeleteCompany } from './ModalDeleteCompany';
import { ModalDeactivateCompany } from './ModalDeactivateCompany';
import { ModalParentCompany } from './ModalParentCompany';
import RichTextEditorField from 'components/RichTextEditorField';

const CustomDivider = ({ text }: { text: string }) => {
  return (
    <Divider
      textAlign="left"
      sx={{
        ' :before': {
          width: '0 !important',
        },
        width: '93% !important',
        ' :after': {
          width: '100% !important',
        },
        '&::before, &::after': {
          borderColor: '#213B54',
        },
        color: '#213B54',
        fontWeight: 700,
        fontSize: '14px',
        '& .MuiDivider-wrapper': {
          paddingLeft: '0px',
        },
        height: '21px',
      }}
    >
      {text}
    </Divider>
  );
};

export const DetailFormCompany = () => {
  const [modalDeleteDialog, setModalDeleteDialog] = useState(false);
  const [modalActiveDialog, setModalActiveDialog] = useState(false);
  const [modalParentDialog, setModalParentDialog] = useState(false);
  const [value, setValue] = useState(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [file, setFile] = useState<File>();
  const [links, setLinks] = useState<CompanyRegistrationLink[]>();
  const { company_id, parent_id } = useParams();
  const dispatch = useDispatch();
  const companyInfo: CompanyInfo = useSelector(selectCompany);

  const [photoUrl, setPhotoUrl] = useState<string | undefined>(
    companyInfo.logo,
  );
  const [isParentCompany, setIsParentCompany] = React.useState(false);
  const [parentCompany, setParentCompany] = React.useState<
    CompanyOverview | undefined
  >(companyInfo?.parentCompanyObj || undefined);

  const purchasedEventTypes = useAppSelector(
    (state) => state.company.purchasedEventTypes,
  );
  const { handleSubmit, reset, watch, control, clearErrors } = useForm({
    mode: 'onChange',
  });

  const fetchLinks = async (companyId?: string) => {
    try {
      if (!companyId) return;
      setLinks(await getCompanyLinkNonEmployeeSponsoredAccount(companyId));
    } catch (e) {
      console.log(e);
    }
  };

  const handleUploadLogo = async (event: ChangeEvent) => {
    const files = (event.target as HTMLInputElement).files;
    if (files?.length) {
      const fileCurrent = files[0];
      const ACCEPT_TYPES = ['image/png', 'image/jpeg'];
      if (!ACCEPT_TYPES.includes(fileCurrent.type)) {
        toast.error(
          'This file type is not supported. The following file types are supported: jpeg, png!',
        );
        return;
      }
      const FILE_MAX_SIZES = 5;
      const isLimitSize = fileCurrent.size / 1024 / 1024 < FILE_MAX_SIZES;
      if (!isLimitSize) {
        toast.error(
          `The file size is too large. The maximum file size is ${FILE_MAX_SIZES}MB.`,
        );
        return;
      }
      try {
        if (company_id) {
          const dataSignedPut: SignedPutUrl = await getSignedPutUrl(
            company_id,
            fileCurrent.type,
            isActive,
          );
          await uploadLogoCompanyToAWS(dataSignedPut.signedPutUrl, fileCurrent);
          if (companyInfo.logo) {
            const parsedCompanyLogo = new URL(companyInfo.logo);
            const origin = parsedCompanyLogo?.origin;
            const updatedUrl = `${origin}/${dataSignedPut.key}`;
            setPhotoUrl(updatedUrl);
          }
          toast.success('Upload success!');
        } else {
          setPhotoUrl(URL.createObjectURL(fileCurrent));
          setFile(fileCurrent);
        }
      } catch (error) {
        console.error(error);
        toast.error('Upload fail!');
      }
    }
  };

  const uploadLogoForCreateCompanyToAws = async (
    companyId?: string,
    isActive?: boolean,
    file?: File,
    companyInfo?: CompanyInfo,
  ) => {
    if (companyId && file) {
      const dataSignedPut: SignedPutUrl = await getSignedPutUrl(
        companyId,
        file.type,
        isActive ?? false,
      );
      await uploadLogoCompanyToAWS(dataSignedPut.signedPutUrl, file);
      if (companyInfo?.logo) {
        const parsedCompanyLogo = new URL(companyInfo?.logo);
        const origin = parsedCompanyLogo?.origin;
        const updatedUrl = `${origin}/${dataSignedPut.key}`;
        setPhotoUrl(updatedUrl);
      }
    }
  };

  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  const handleCreateOrUpdateCompany = async () => {
    try {
      const {
        name,
        policyDescription,
        defaultState,
        slug,
        companyType,
        address1,
        address2,
        city,
        zipCode,
        generalEmail,
        logo,
        companyPhone,
        googleReviewLink,
        companyPurchasedEventsTypeIds,
        supportServices,
        communicationNotes,
      } = watch();
      if (!company_id) {
        await createCompany({
          name,
          policyDescription,
          defaultState,
          slug,
          companyType,
          address1,
          address2,
          city,
          zipCode,
          generalEmail,
          logo,
          isActive: isActive,
          companyPhone: companyPhone?.replaceAll(/\D/g, ''),
          googleReviewLink,
          companyPurchasedEventsTypeIds,
          isParentCompany: isParentCompany ?? false,
          parentCompany: parent_id ? parent_id : parentCompany?.companyId,
          supportServices,
          communicationNotes,
        })
          .then(async (data: CompanyInfo) => {
            if (data && file) {
              await uploadLogoForCreateCompanyToAws(
                data.companyId,
                isActive,
                file,
                data,
              );
            }
            toast.success('Create company successfully!');
            dispatch(saveCompany(data));
            if (parent_id) {
              window.location.href = `${DASHBOARD_ROUTES.COMPANY_BRANCH_LOCATIONS(
                companyInfo.companyId,
              )}`;
            } else {
              window.location.href = `${
                DASHBOARD_ROUTES.COMPANY_DETAIL + data.companyId
              }`;
            }
          })
          .catch((error) => {
            console.error(error);
            toast.error('Create company fail!');
          });
      } else {
        await updateCompany(company_id, {
          companyId: company_id,
          name,
          policyDescription,
          defaultState,
          slug,
          companyType,
          address1,
          address2,
          city,
          zipCode,
          generalEmail,
          logo,
          isActive: isActive,
          companyPhone: companyPhone?.replaceAll(/\D/g, ''),
          googleReviewLink,
          companyPurchasedEventsTypeIds,
          isParentCompany: isParentCompany ?? false,
          parentCompany: parentCompany?.companyId,
          supportServices,
          communicationNotes,
        })
          .then((data) => {
            toast.success('Update company successfully!');
            dispatch(saveCompany(data));
            window.location.href = `${
              DASHBOARD_ROUTES.COMPANY_DETAIL + company_id
            }`;
          })
          .catch((error) => {
            console.error(error);
            toast.error('Update company fail!');
          });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmitForm = async () => {
    try {
      if (!isActive) {
        setValue(true);
        setModalActiveDialog(true);
      } else if (isParentCompany && parentCompany) {
        setModalParentDialog(true);
      } else {
        handleCreateOrUpdateCompany();
      }
    } catch (error) {
      toast.error('error');
    }
  };

  const resetForm = () => {
    reset({
      companyId: !parent_id ? companyInfo?.companyId || '' : '',
      name: !parent_id ? companyInfo?.name || '' : '',
      companyType: !parent_id ? companyInfo?.companyType || '' : '',
      address1: !parent_id ? companyInfo?.address1 || '' : '',
      address2: !parent_id ? companyInfo?.address2 || '' : '',
      city: !parent_id ? companyInfo?.city || '' : '',
      defaultState: !parent_id ? companyInfo?.defaultState || '' : '',
      zipCode: !parent_id ? companyInfo?.zipCode || '' : '',
      generalEmail: !parent_id ? companyInfo?.generalEmail || '' : '',
      policyDescription: !parent_id ? companyInfo?.policyDescription || '' : '',
      registrationLink: !parent_id ? companyInfo?.registrationLink || '' : '',
      logo: !parent_id ? companyInfo?.logo || photoUrl || '' : '',
      isActive: !parent_id ? companyInfo?.isActive ?? false : false,
      slug: !parent_id ? companyInfo?.slug || '' : '',
      companyPhone: !parent_id ? companyInfo?.companyPhone || '' : '',
      googleReviewLink: !parent_id ? companyInfo?.googleReviewLink || '' : '',
      companyPurchasedEventsTypeIds: !parent_id
        ? companyInfo?.companyPurchasedEventsTypeIds || []
        : [],
      parentCompany: !parent_id ? companyInfo.parentCompany || '' : '',
      supportServices: !parent_id ? companyInfo.supportServices || '' : '',
      communicationNotes: !parent_id
        ? companyInfo.communicationNotes || ''
        : '',
    });
    clearErrors();
  };

  React.useEffect(() => {
    if (company_id) {
      fetchLinks(company_id);
    }
  }, [company_id]);

  React.useEffect(() => {
    resetForm();
    setPhotoUrl(!parent_id ? companyInfo.logo : '');
    setIsActive(!parent_id ? companyInfo.isActive ?? false : false);
    if (companyInfo?.parentCompanyObj?.companyId) {
      setParentCompany({
        companyId: companyInfo?.parentCompanyObj?.companyId || '',
        rank: companyInfo?.parentCompanyObj?.rank || 0,
        name: companyInfo?.parentCompanyObj?.name || '',
        employeeCount: companyInfo?.parentCompanyObj?.employeeCount || 0,
        createdDate: companyInfo?.parentCompanyObj?.createdDate || '',
      });
    } else if (parent_id) {
      setParentCompany({
        companyId: companyInfo?.companyId || '',
        rank: companyInfo?.rank || 0,
        name: companyInfo?.name || '',
        employeeCount: companyInfo?.employeeCount || 0,
        createdDate: companyInfo?.createdDate || '',
      });
    } else {
      setParentCompany(undefined);
    }
    setIsParentCompany(
      !parent_id ? companyInfo.isParentCompany ?? false : false,
    );
  }, [companyInfo]);

  React.useEffect(() => {
    if (!purchasedEventTypes || purchasedEventTypes.length === 0) {
      dispatch(
        getPurchasedEventType(company_id || companyInfo.companyId || ''),
      );
    }
  }, [purchasedEventTypes, company_id]);

  return (
    <Stack
      sx={{
        alignItems: 'start',
        width: '100%',
      }}
    >
      <form onSubmit={handleSubmit(handleSubmitForm)} className="w-full">
        <Stack
          direction={'row'}
          sx={{
            order: { xs: 3, md: 1 },
            width: '93%',
            marginTop: '24px',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              fontWeight: 500,
              fontSize: '15px',
              lineHeight: '24px',
              letterSpacing: '0.5px',
              color: '#000000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
            }}
          >
            <CheckboxSingle
              name="isActive"
              label="Active"
              checked={isActive}
              onChange={(event) => {
                setIsActive(event.target.checked);
                setValue(false);
              }}
            />
          </Box>
          <Stack direction={'row'}>
            <Button
              sx={{
                border: '1px solid #213B54',
                color: '#213B54',
                fontWeight: 700,
                display: company_id ? '' : 'none',
                padding: '10px 20px',
                width: '104px',
              }}
              onClick={() => {
                setModalDeleteDialog(true);
              }}
            >
              Delete
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{
                fontWeight: 700,
                marginLeft: '8px',
                width: '104px',
                padding: '10px 30px ',
              }}
            >
              Save
            </Button>
          </Stack>
        </Stack>
        <Grid
          container
          sx={{
            marginY: '24px',
          }}
        >
          <Grid item xs={12} md={6}>
            <Stack
              sx={{
                width: '86%',
                gap: 2,
                marginBottom: '24px',
              }}
            >
              <TextField
                label="Name"
                name="name"
                control={control}
                required
                placeholder="Name"
                rules={{
                  validate: (value) => {
                    const regex = /^[a-zA-Z0-9.'\s-]{2,50}$/;
                    if (!value) return true;
                    if (
                      !value?.trim() ||
                      value?.trim()?.length < 2 ||
                      regex.test(value?.trim()) === false
                    )
                      return 'Please enter 2 ~ 50 letters of a-z, A-Z, 0-9, Period, Single quote, Whitespace, or Hyphen';
                    return true;
                  },
                  required: 'Name is required.',
                }}
              />
              <Select
                data={[
                  {
                    value: CompanyType.Employer,
                    label: CompanyType.Employer,
                  },
                  {
                    value: CompanyType.Hospice,
                    label: CompanyType.Hospice,
                  },
                  {
                    value: CompanyType.LongtermCare,
                    label: CompanyType.LongtermCare,
                  },
                  {
                    value: CompanyType.DeathCare,
                    label: CompanyType.DeathCare,
                  },
                  {
                    value: CompanyType.HealthSystem,
                    label: CompanyType.HealthSystem,
                  },
                  {
                    value: CompanyType.HealthPlan,
                    label: CompanyType.HealthPlan,
                  },
                ]}
                control={control}
                showSelected={true}
                label="Company Type"
                name="companyType"
                rules={{
                  required: 'Company Type is required.',
                }}
                required
              />
              <TextField
                label="General Email"
                control={control}
                name="generalEmail"
                placeholder="General Email"
                type="email"
              />
              <TextFieldWithMask
                label="Phone"
                placeholder="Type Phone"
                format="(###) ### ####"
                control={control}
                name="companyPhone"
                rules={{
                  required: 'Type Phone is required.',
                }}
                required
              />
              <TextField
                label="Google Review Link"
                control={control}
                name="googleReviewLink"
                placeholder="Google Review Link"
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 500,
                  fontSize: '15px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                  color: '#000000',
                }}
              >
                {`https://app.betterleave.com/`}
                <TextField
                  label="Landing Page URL"
                  name="slug"
                  sx={{
                    marginLeft: '4px',
                    width: '86%',
                  }}
                  control={control}
                  placeholder="Landing Page URL"
                />
              </Box>
              {company_id && (
                <TextField
                  label="Registration Link"
                  sx={{
                    width: '100%',

                    label: {
                      color: 'rgba(0, 0, 0, 0.38)',
                    },
                    input: { cursor: 'pointer' },
                  }}
                  name="registrationLink"
                  placeholder="Registration link"
                  inputProps={{
                    onClick: () =>
                      links?.length !== 0 &&
                      window.open(
                        links ? links[0]?.url : '',
                        '_blank',
                        'noopener,noreferrer',
                      ),
                  }}
                  value={links ? links[0]?.url : ''}
                />
              )}
              <Stack>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '12px',
                    lineHeight: '16px',
                    letterSpacing: '0.5px',
                  }}
                >
                  Company Logo
                </Typography>
                <Button
                  component="label"
                  sx={{
                    width: '220px',
                    height: '108px !important',
                    color: '#000000 !important',
                    background: '#D9D9D97A !important',
                    border: '2px solid #e9ebef',
                    borderRadius: '4px !important',
                    cursor: 'pointer',
                    margin: '3px 0 0 ',
                    textAlign: 'center',
                    fontWeight: 500,
                  }}
                >
                  {photoUrl && !parent_id ? (
                    <img
                      key={Date.now()}
                      src={`${photoUrl}`}
                      style={{
                        display: 'block',
                        height: '108px',
                        width: '220px',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <>Upload file</>
                  )}

                  <VisuallyHiddenInput
                    onChange={handleUploadLogo}
                    name="photoUrl"
                    type="file"
                  />
                </Button>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack
              sx={{
                width: '86%',
                gap: 2,
              }}
            >
              {purchasedEventTypes && purchasedEventTypes.length > 0 && (
                <MultiSelect
                  name="companyPurchasedEventsTypeIds"
                  label="Purchased Event Types"
                  control={control}
                  data={
                    (purchasedEventTypes &&
                      purchasedEventTypes.length > 0 &&
                      purchasedEventTypes.map((item) => ({
                        value: item.eventId,
                        label: item.abbreviatedName || '',
                      }))) ||
                    []
                  }
                />
              )}
              <TextField
                label="Address 1"
                name="address1"
                control={control}
                placeholder="Address 1"
              />
              <TextField
                label="Address 2"
                name="address2"
                control={control}
                placeholder="Address 2"
              />
              <TextField
                label="City"
                control={control}
                name="city"
                placeholder="City"
              />
              <Select
                data={STATES.filter((state) => {
                  return state.name != 'All';
                }).map((item) => ({
                  value: item.abbreviation,
                  label: item.name,
                }))}
                control={control}
                showSelected={true}
                label="State"
                name="defaultState"
              />
              <TextFieldNumber
                autoComplete="off"
                name="zipCode"
                label="Zip Code"
                control={control}
                inputProps={{
                  maxLength: 5,
                }}
                rules={{
                  validate: (value) => {
                    if (!value) return true;
                    if (value?.length !== 5)
                      return 'The US zip code must contain 5 digits.';
                    return true;
                  },
                }}
              />
              <TextField
                name="supportServices"
                label="Support Services"
                control={control}
                placeholder="Support Services"
                multiline
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#79747E !important',
                  },
                  '& .Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#B3261E !important',
                  },
                  input: {
                    height: '24px !important',
                  },
                  textarea: {
                    padding: '0px !important',
                  },
                }}
                rows={5}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} marginTop={'24px'}>
            <Stack sx={{ width: '93%' }}>
              <TextField
                name="communicationNotes"
                label="Communication Notes"
                control={control}
                placeholder="Communication Notes"
                helperText="Internal notes for the care team about this company that affect how we communicate with family members. Not visible to customers or families. Include sensitivities, do's/don'ts, tone, or context that should shape outreach. Auto-drafted replies will take these notes into consideration."
                multiline
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#79747E !important',
                  },
                  // Four sentences of guidance, so it reads at body scale
                  // rather than at MUI's 12px caption default.
                  '& .MuiFormHelperText-root': {
                    fontSize: '13px',
                    lineHeight: '18px',
                    marginTop: '6px',
                  },
                  textarea: {
                    padding: '0px !important',
                    // Fixed rows and a scrollbar rather than a box that grows:
                    // these notes run long, and a reader has to be able to tell
                    // there is more below the fold. The gutter is painted
                    // explicitly because macOS hides overlay scrollbars at rest.
                    overflowY: 'auto !important',
                    '&::-webkit-scrollbar': { width: '8px' },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#C4C7CA',
                      borderRadius: '4px',
                    },
                  },
                }}
                rows={8}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} marginBottom={'8px'} marginTop={'12px'}>
            <CustomDivider text="Parent Company Details" />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack
              sx={{
                width: '86%',
                gap: '16px',
              }}
            >
              <Stack
                direction={'row'}
                sx={{
                  alignItems: 'center',
                }}
              >
                <MaterialUISwitch
                  sx={{
                    border: '2px solid #213B54',
                    borderRadius: '100px',
                    marginRight: '12px',
                  }}
                  disabled={!!parent_id}
                  onChange={() => {
                    setIsParentCompany(!isParentCompany);
                  }}
                  checked={isParentCompany}
                />
                <Typography>Has Branch Locations</Typography>
              </Stack>
              <SearchBox
                label="Parent Company"
                disabled={parent_id ? true : isParentCompany}
                setCompany={(company: CompanyOverview) => {
                  if (!company.companyId) {
                    setParentCompany(undefined);
                  } else
                    setParentCompany({
                      companyId: company.companyId,
                      rank: company.rank,
                      name: company.name,
                      employeeCount: company.employeeCount,
                      createdDate: company.createdDate,
                    });
                }}
                defaultValue={parentCompany}
                isParentCompany={true}
              />
            </Stack>
          </Grid>
        </Grid>
      </form>
      <ModalDeleteCompany
        modalDeleteDialog={modalDeleteDialog}
        setModalDeleteDialog={setModalDeleteDialog}
      />
      <ModalDeactivateCompany
        modalActiveDialog={modalActiveDialog}
        setModalActiveDialog={setModalActiveDialog}
        handleCreateOrUpdateCompany={handleCreateOrUpdateCompany}
        setIsActive={setIsActive}
        setModalParentDialog={setModalParentDialog}
        isParentCompany={isParentCompany}
        value={value}
        parentCompany={parentCompany}
      />
      <ModalParentCompany
        modalParentDialog={modalParentDialog}
        setModalParentDialog={setModalParentDialog}
        handleSubmitForm={handleCreateOrUpdateCompany}
      />
    </Stack>
  );
};
